import React from 'react';
import { Entity } from '@backstage/catalog-model';
import {
  EmptyState,
  Link,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { entityRouteRef, useEntityList } from '@backstage/plugin-catalog-react';
import { Typography, makeStyles } from '@material-ui/core';
import { PlatformStructureTitles } from './PlatformServicePickers';
import { ServiceCard } from './ServiceCard';

const useStyles = makeStyles(theme => ({
  domains: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
  },
  heading: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  // The same grid as the one of the team cards.
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: theme.spacing(2),
    alignItems: 'stretch',
  },
}));

/**
 * Props for {@link ServiceCardGrid}.
 *
 * @public
 */
export type ServiceCardGridProps = {
  /** The titles of the domains and systems, see usePlatformStructureTitles. */
  titles: PlatformStructureTitles;
};

const titleOf = (entity: Entity) =>
  entity.metadata.title ?? entity.metadata.name;

/** The domains, in the order they are shown, each with its services. */
type DomainGroup = { domain?: string; title: string; services: Entity[] };

/**
 * The services by domain, the domains sorted by title and those without one
 * last, the services of each sorted by title.
 */
export function groupByDomain(
  services: Entity[],
  domainTitles: Record<string, string | undefined>,
): DomainGroup[] {
  const groups = new Map<string | undefined, Entity[]>();
  for (const service of services) {
    const domain = (service.spec?.domain as string | undefined) || undefined;
    groups.set(domain, [...(groups.get(domain) ?? []), service]);
  }
  return [...groups.entries()]
    .map(([domain, members]) => ({
      domain,
      title: domain ? (domainTitles[domain] ?? domain) : 'No domain',
      services: [...members].sort((a, b) =>
        titleOf(a).localeCompare(titleOf(b)),
      ),
    }))
    .sort((a, b) => {
      if (!a.domain || !b.domain) {
        return a.domain ? -1 : 1;
      }
      return a.title.localeCompare(b.title);
    });
}

/**
 * The platform services the surrounding listing holds, a card each, under a
 * heading per domain. Each card names the system above the service; the
 * filters narrow the gallery down.
 *
 * @public
 */
export const ServiceCardGrid = (props: Readonly<ServiceCardGridProps>) => {
  const { titles } = props;
  const classes = useStyles();
  const entityRoute = useRouteRef(entityRouteRef);
  const { entities, loading, error } = useEntityList();

  if (loading) {
    return <Progress />;
  }
  if (error) {
    return <ResponseErrorPanel error={error} />;
  }
  if (entities.length === 0) {
    return (
      <EmptyState
        missing="data"
        title="No services found"
        description="No platform service matches the filters."
      />
    );
  }

  const systemOf = (service: Entity) => {
    const system = service.spec?.system as string | undefined;
    return system ? (titles.systems[system] ?? system) : undefined;
  };

  return (
    <div className={classes.domains}>
      {groupByDomain(entities, titles.domains).map(group => (
        <section key={group.domain ?? ''}>
          <div className={classes.heading}>
            <Typography variant="h5" component="h2">
              {group.domain ? (
                <Link
                  to={entityRoute({
                    kind: 'domain',
                    namespace: 'default',
                    name: group.domain,
                  })}
                  color="inherit"
                >
                  {group.title}
                </Link>
              ) : (
                group.title
              )}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {group.services.length}
            </Typography>
          </div>
          <div className={classes.grid}>
            {group.services.map(service => (
              <ServiceCard
                key={service.metadata.name}
                entity={service}
                system={systemOf(service)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};
